import { EyeOutlined } from "@ant-design/icons";
import { HasAccess } from "@permify/react-role";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Popover,
  Row,
  Select,
  Spin,
  Tooltip,
  message,
} from "antd";
import locale from 'antd/es/date-picker/locale/es_ES';
import parsePhoneNumber from 'libphonenumber-js';
import moment from "moment";
import 'moment/locale/es';
import { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { handlers } from "../../../../helpers/helpers";
import { consultationService } from "../../../../services/medicalConsultation";
import { getCompany, getEducationLevel } from "../../../../services/general";
import { getCookie } from "../../../../services/cookies";
import useRecommendationsModal from '../../../components/hooks/useRecommendationsModal';
import usePendingExams from "../../hooks/usePendingExams";
import ConclusionsModal from '../ConclusionsModal';
import SignButton from "../SignButton";

const { TextArea } = Input;
const { Option } = Select;
const { format } = require('rut.js');

const PsychologicalTwo = ({
  id,
  form,
  disableForm,
  servicesList,
  serviceIndex,
  onFinishConsultation,
  generateReport,
  setIsEditingMode,
  onFormPopulated,
  loadingConsultation,
  medicalConsultationDetails,
  patientData,
  patientFullName,
  unfinishedExams,
  countryCode,
  approvedTextList,
  loading,
  listReproved,
  isUnableToChoose,
  hasToShowPopOver,
  setStatus,
  oldReport,
  oldReportCountry,
  countrySettings,
  showConfirmReprovingValues,
  setShowConfirmReprovingValues,
  validatedExams,
  generatedPrescriptions,
  hasDeferredExamPrescription,
}) => {
  const [loadingFetchs, setLoadingFetchs] = useState({
    education: false,
    patient: false,
    company: false,
  });
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [pendingReasons, setPendingReasons] = useState();
  const [companyData, setCompanyData] = useState({
    name: '',
    document_type: '',
    document_number: '',
  });
  const [educationLevelList, setEducationLevelList] = useState();
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [showReprovePopover, setShowReprovePopover] = useState(false);
  const [conclusionPosition, setConclusionPosition] = useState();
  const [resultConclusionText, setResultConclusionText] = useState("");
  const { t } = useTranslation();

  const recommendatiosModal = useRecommendationsModal({
    t,
    service: servicesList[serviceIndex],
    patientFullName,
    countryCode,
    conclusionPosition,
    setConclusionText: setResultConclusionText,
    medicalForm: form,
    countrySettings,
  });

  const fetchEducationLevelList = useCallback(async () => {
    setLoadingFetchs(loadingFetchs => { return { ...loadingFetchs, education: true } });
    const response = await getEducationLevel({ countryCode });
    setEducationLevelList(response);
    setLoadingFetchs(loadingFetchs => { return { ...loadingFetchs, education: false } });
  }, []);

  const fetchCompany = useCallback(async (params) => {
    const response = await getCompany(params);

    setCompanyData({
      name: response[0].business_name,
      document_type: response[0].document_type,
      document_number: response[0].document_type === 'rut'
        ? format(response[0].document_number)
        : response[0].document_number
    });
  }, []);

  const fetchPendingReasons = useCallback(async () => {
    setLoadingReasons(true);
    try {
      const response = await consultationService.getPendingReasons({ department_id: 2 });
      setPendingReasons(response);
    } catch (error) {
      console.error(error);
      message.error(t('medicalConsultation.consultationEdit.errorFetchingPendingReasons'));
    } finally {
      setLoadingReasons(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPendingReasons();
  }, [fetchPendingReasons]);

  const fetchBatteryValidityValues = useCallback(async (serviceId) => {
    const { dateEvaluation, validityEvaluation } = form.getFieldsValue(['dateEvaluation', 'validityEvaluation']);

    if (dateEvaluation && validityEvaluation) {
      return;
    }

    if (!countrySettings.medicalConsultation.autoFillBatteryValidity[countryCode]) {
      return;
    }

    try {
      const response = await consultationService.getBatteryValidity({ id: serviceId, admissionPatientId: id });
      const formatDate = (date) => moment(date, "YYYY-MM-DD");
      if (response && response.date_evaluation && response.validity_evaluation) {
        form.setFieldsValue({
          dateEvaluation: dateEvaluation || formatDate(response.date_evaluation),
          validityEvaluation: validityEvaluation || formatDate(response.validity_evaluation)
        });
      }
    } catch (error) {
      console.error(error);
      message.error(t('medicalConsultation.consultationEdit.errorFetchingBatteryValidity'));
    }
  }, [form, id, countryCode, countrySettings]);

  useEffect(() => {
    if (!educationLevelList) {
      fetchEducationLevelList();
    }
  }, [fetchEducationLevelList]);

  const getPhoneNumber = (phone) => {
    if (phone) {
      const parsedNumber = parsePhoneNumber(phone);
      if (parsedNumber === undefined) {
        return `+56 ${56}`;
      }
    }
    return phone;
  };

  useEffect(() => {
    form.setFieldsValue({
      educationalLvl: patientData?.educational_lvl,
    });
  }, [patientData])

  useEffect(() => {
    const selectedService = servicesList[serviceIndex];
    const formatDate = (date) => {
      return moment(date, "YYYY-MM-DD");
    };
    if (medicalConsultationDetails && selectedService.medical_consultation_id) {
      const medicalConsultationStatus = approvedTextList?.find(approvedText =>
        approvedText.id === medicalConsultationDetails.consultation_result_id
      );

      setStatus(medicalConsultationStatus)

      form.setFieldsValue({
        dateEvaluation: medicalConsultationDetails['date_evaluation'] && formatDate(medicalConsultationDetails['date_evaluation']),
        validityEvaluation: medicalConsultationDetails['validity_evaluation'] && formatDate(medicalConsultationDetails['validity_evaluation'], "YYYY-MM-DD"),
        appliedEvaluation: medicalConsultationDetails.applied_evaluation || selectedService.exams_description,
        pendingReasons: medicalConsultationDetails["pending_reasons"]?.filter(reason => reason != null),
        conclusion: medicalConsultationDetails['conclusion'],
        indication: medicalConsultationDetails.indications,
        suggestion: medicalConsultationDetails.suggestions,
        status: medicalConsultationDetails.consultation_result_id,
      });
      setShowReprovePopover(medicalConsultationStatus?.status === "reproved");
    } else {
      form.setFieldsValue({
        appliedEvaluation: selectedService.exams_description
      });
    }

    if (selectedService && selectedService.id) {
      fetchBatteryValidityValues(selectedService.id);
    }
    if (selectedService && selectedService.company_id) {
      fetchCompany({ id: selectedService.company_id });
    }

    form.setFieldsValue({
      id: selectedService["medical_consultation_id"] ? selectedService["medical_consultation_id"] : null,
      serviceId: selectedService["id"],
      serviceType: selectedService["type"],
      admissionPatientId: parseInt(id),
    });
    onFormPopulated?.(form.getFieldsValue());
  }, [serviceIndex, medicalConsultationDetails, servicesList, id, form, fetchCompany, approvedTextList, fetchBatteryValidityValues]);

  const saveText = (consultation) => {
    if (consultation && consultation["revaluation"]) {
      return "Editar Reevaluación";
    }

    if (consultation && consultation["state"] === "readyToSign") {
      return "Editar";
    }

    return "Guardar";
  };

  const downloadSignedReport = async (action, id, type) => {
    try {
      setLoadingDownload(true);
      const fileName = `${patientFullName}_${patientData?.document_number}_${id}.pdf`
      await handlers.downloadSignedReport({
        id,
        type,
        countryCode,
        fileName,
        action,
      })
    } catch (error) {
      console.error(error);
      message.error({ content: "Error al descargar informe", style: { marginTop: "35vh", fontSize: 16, }, });
    } finally {
      setLoadingDownload(false);
    }
  };

  const downloadUnSignedReport = (id) => {
    setLoadingDownload(true);
    generateReport(id, undefined, false, false)
      .then((response) => {
        handlers.base642Open(response["pdf"]);
      })
      .finally(() => {
        setLoadingDownload(false);
      });
  };

  const fieldsMessages = {
    'educationalLvl': t('medicalConsultations.psychologicalTwoForm.educationalLvlMessage'),
    'dateEvaluation': t('medicalConsultations.psychologicalTwoForm.dateEvaluationMessage'),
    'validityEvaluation': t('medicalConsultations.psychologicalTwoForm.validityEvaluationMessage'),
    'appliedEvaluation': t('medicalConsultations.psychologicalTwoForm.appliedEvaluationMessage'),
    'status': t('medicalConsultations.psychologicalTwoForm.approvedTextMessage'),
    'pendingReasons': t('medicalConsultations.healthForm.pendingReasonsMessage'),
    'conclusion': t('medicalConsultations.psychologicalTwoForm.conclusionMessage')
  };

  const rulesForRequiredFields = (field) => {
    return {
      required: true,
      message: fieldsMessages[field],
    };
  }

  const validateCompletedReport = () => {
    form.validateFields()
      .then(value => {
        form.setFieldsValue({ sign: true });
      });
  };

  const pendingExams = usePendingExams(unfinishedExams, medicalConsultationDetails, disableForm);

  const handleOnSelect = (value) => {
    setShowReprovePopover(approvedTextList.find(approvedText => approvedText.id === value).status === "reproved");
  }

  const handleResultState = (selectedOption) => {
    const status = approvedTextList?.find((option) =>
      option.id === selectedOption
    );
    form.setFieldsValue({ pendingReasons: undefined });
    setStatus(status);

    let currentConclusion = "";

    if (countrySettings.medicalConsultation.useConclusionFromSelectedStatus[countryCode]) {
      currentConclusion = status.text || '';
    }

    if (countrySettings.medicalConsultation.usePredefinedPsychologyText[countryCode]) {
      let predefined = "";

      if (status.status.includes('approved')) {
        predefined = servicesList[serviceIndex].approved_text || '';
        if (countrySettings.medicalConsultation.applySecondaryStatusActions[countryCode]) {
          setShowReprovePopover(false);
          if (servicesList[serviceIndex].is_provider) {
            form.setFieldsValue({
              secondary_conclusion: servicesList[serviceIndex].secondary_approved_text
            });
          }
        }
      } else if (status.status.includes('reproved')) {
        predefined = servicesList[serviceIndex].reprobate_text || '';
        if (countrySettings.medicalConsultation.applySecondaryStatusActions[countryCode]) {
          setShowReprovePopover(servicesList[serviceIndex].type === "Battery");
          if (servicesList[serviceIndex].is_provider) {
            form.setFieldsValue({
              secondary_conclusion: servicesList[serviceIndex].secondary_disapproved_text
            });
          }
        }
      }

      if (predefined) {
        if (currentConclusion) {
          currentConclusion += `\n${predefined}`;
        } else {
          currentConclusion = predefined;
        }
      }
    }

    form.setFieldsValue({
      conclusion: currentConclusion
    });
  };

  const handleConclusionInput = (event) => {
    setResultConclusionText(event.target.value);
  };

  return (
    <Fragment>
      <Form
        form={form}
        onFinish={onFinishConsultation}
        onFieldsChange={() => setIsEditingMode(true)}
        size="large"
        layout="vertical"
      >
        <Form.Item name="id" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="serviceId" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="serviceType" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="admissionPatientId" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Row gutter={[0, 20]}>
          <Col lg={20} xs={24}>
            <Row>
              <Col lg={24} xs={24}>
                <p className="bold">{t('medicalConsultations.psychologicalTwoForm.title')}</p>
              </Col>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="educationalLvl"
                  label={t('medicalConsultations.psychologicalTwoForm.educationalLvlLabel')}
                  rules={
                    countrySettings?.medicalConsultation?.educationalLevelRequired?.[countryCode]
                      ? [rulesForRequiredFields('educationalLvl')]
                      : []
                  }
                >
                  {loadingFetchs['education'] ? (
                    <Row justify="center">
                      <Spin className="cmt-spin" />
                    </Row>
                  ) : (
                    <Select
                      id="educationLvlSelect"
                      placeholder={t('medicalConsultations.psychologicalTwoForm.educationalLvlPlaceholder')}
                      className="cmt-select"
                      disabled={disableForm}
                    >
                      {educationLevelList?.map((item, index) => {
                        return (
                          <Option key={index} value={item}>
                            {item}
                          </Option>
                        );
                      })}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col lg={24} xs={24}>
                <p>
                  {t('medicalConsultations.psychologicalTwoForm.patientProfession')}: <span className="ml-10">{patientData?.position}</span>
                </p>
                <p>
                  {t('medicalConsultations.psychologicalTwoForm.patientPhone')}: <span className="ml-10">{getPhoneNumber(patientData?.phone)}</span>
                </p>
              </Col>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="dateEvaluation"
                  label={t('medicalConsultations.psychologicalTwoForm.dateEvaluationLabel')}
                  rules={[rulesForRequiredFields('dateEvaluation')]}
                >
                  <DatePicker
                    id="dateEvalDatePicker"
                    className="date-picker"
                    locale={locale}
                    placeholder={t('medicalConsultations.psychologicalTwoForm.dateEvaluationPlaceholder')}
                    style={{ width: '100%' }}
                    disabled={disableForm}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </Col>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="validityEvaluation"
                  label={t('medicalConsultations.psychologicalTwoForm.validityEvaluationLabel')}
                  rules={[rulesForRequiredFields('validityEvaluation')]}
                >
                  <DatePicker
                    id="validityDatePicker"
                    className="date-picker"
                    locale={locale}
                    placeholder={t('medicalConsultations.psychologicalTwoForm.validityEvaluationPlaceholder')}
                    style={{ width: '100%' }}
                    disabled={disableForm}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col lg={20} xs={24}>
            {companyData && (
              <Row>
                <p className="bold">{t('medicalConsultations.psychologicalTwoForm.companyDataTitle')}</p>
                <Col lg={24} xs={24}>
                  <p>
                    {t('medicalConsultations.psychologicalTwoForm.companyName')}: <span className="ml-10">{companyData && companyData['name']}</span>
                  </p>
                </Col>
                <Col lg={24} xs={24}>
                  <p>
                    {companyData?.document_type?.toLowerCase() === 'passport' ? 'Pasaporte' : companyData?.document_type?.toUpperCase()}:
                    <span className="ml-10">{companyData?.document_number}</span>
                  </p>
                </Col>
              </Row>
            )}
          </Col>
          <Col lg={24} xs={24}>
            <Row>
              <p className="bold">{t('medicalConsultations.psychologicalTwoForm.appliedEvaluations')}</p>
              <Col lg={24} xs={24}>
                <Form.Item name="appliedEvaluation" rules={[rulesForRequiredFields('appliedEvaluation')]}>
                  <TextArea className="cmt-input" style={{ height: 140 }} disabled={disableForm} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col lg={24} xs={24}>
            <Row>
              <Col lg={24} xs={24}>
                <p className="bold">{t('medicalConsultations.psychologicalTwoForm.evaluation')}</p>
              </Col>
              <Col lg={20} xs={24}>
                <Form.Item
                  name="status"
                  label={t('medicalConsultations.psychologicalTwoForm.approvedTextLabel')}
                  rules={[rulesForRequiredFields('status')]}
                >
                  {loading.text ? (
                    <Row justify="center">
                      <Spin className="cmt-spin" />
                    </Row>
                  ) : (
                    <Select
                      placeholder={t('medicalConsultations.psychologicalTwoForm.approvedTextPlaceholder')}
                      className="cmt-select"
                      onSelect={handleOnSelect}
                      onChange={handleResultState}
                      disabled={disableForm && disableForm}
                    >
                      {approvedTextList?.map((option) =>
                        option.status === 'approved' && hasToShowPopOver() ? (
                          <Option key={option.id} value={option.id}>
                            <Popover
                              title={
                                <strong>
                                  {isUnableToChoose()
                                    ? t('medicalConsultations.consultationEdit.statusAlteredDisclaimerTitle')
                                    : t('medicalConsultations.consultationEdit.statusReprovedDisclaimerTitle')}
                                </strong>
                              }
                              trigger="click"
                              content={listReproved()}
                              overlayClassName="sidebar-tooltip"
                            >
                              {option.text}
                            </Popover>
                          </Option>
                        ) : (
                          <Option key={option.id} value={option.id}>
                            {option.text}
                          </Option>
                        ),
                      )}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col lg={24} xs={24}>
                {approvedTextList?.find((option) => option.id === form.getFieldValue('status'))?.status === 'pending' && (
                  <Form.Item name="pendingReasons" rules={[rulesForRequiredFields('pendingReasons')]}>
                    {loadingReasons ? (
                      <Spin className="cmt-spin" />
                    ) : (
                      <Select
                        className="cmt-select"
                        mode="multiple"
                        placeholder="Seleccione motivo/s"
                        style={{ width: '100%' }}
                        disabled={disableForm}
                      >
                        {pendingReasons?.map((item) => {
                          return (
                            <Option key={item.key} value={item.key}>
                              {item.description}
                            </Option>
                          );
                        })}
                      </Select>
                    )}
                  </Form.Item>
                )}
              </Col>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="conclusion"
                  className="mb-0"
                  label={t('medicalConsultations.psychologicalTwoForm.conclusionLabel')}
                  rules={[rulesForRequiredFields('conclusion')]}
                >
                  <TextArea
                    id="conclusionItem"
                    className={`cmt-input${resultConclusionText?.length >= 1600 && !oldReport && oldReportCountry ? '-error' : ''}`}
                    style={{ height: 140 }}
                    disabled={disableForm}
                    onChange={(event) => handleConclusionInput(event)}
                    maxLength={!oldReport && oldReportCountry && '1600'}
                  />
                </Form.Item>
              </Col>
              {!oldReport && oldReportCountry && (
                <Col lg={24} xs={24} className={`text-right conclusion-alert${resultConclusionText?.length >= 1600 ? '-error' : ''}`}>
                  Máximo 1.600 caracteres
                </Col>
              )}

              <Col lg={24} xs={24}>
                <Row gutter={15} className="mb-20">
                  <Col>
                    <Button
                      onClick={() =>
                        recommendatiosModal.showModal({
                          type: 'indication',
                          from: 'conclusions',
                        })
                      }
                      className="cmt-button secondary"
                      type="primary"
                      size="small"
                      disabled={disableForm}
                    >
                      {t('medicalConsultations.healthForm.indications')}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      id="suggestionsButton"
                      onClick={() =>
                        recommendatiosModal.showModal({
                          type: 'suggestion',
                          from: 'conclusions',
                        })
                      }
                      className="cmt-button secondary"
                      type="primary"
                      size="small"
                      disabled={disableForm}
                    >
                      {t('medicalConsultations.healthForm.suggestions')}
                    </Button>
                  </Col>
                </Row>
              </Col>
              <Col lg={24} xs={24}>
                <Col lg={24} xs={24}>
                  <p className="bold">{t('medicalConsultations.healthForm.indications')}</p>
                </Col>

                <Col lg={24} xs={24}>
                  <Form.Item name="indication">
                    <TextArea className="cmt-input" style={{ height: 140 }} />
                  </Form.Item>
                </Col>
              </Col>
              <Col lg={24} xs={24}>
                <Col lg={24} xs={24}>
                  <p className="bold">{t('medicalConsultations.healthForm.suggestions')}</p>
                </Col>
                <Col lg={24} xs={24}>
                  <Form.Item name="suggestion">
                    <TextArea className="cmt-input" style={{ height: 140 }} />
                  </Form.Item>
                </Col>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="mt-70" gutter={[15, 10]}>
          <HasAccess roles={['super_usuario', 'equipo_psicologia', 'direccion_medica']} permissions="save-medical-consultation">
            <Col xxl={4} lg={6} xs={24}>
              <Form.Item name="save">
                <Button
                  loading={loadingConsultation.save}
                  htmlType="submit"
                  onClick={() => form.setFieldsValue({ save: true })}
                  className="cmt-button primary"
                  type="primary"
                  size="large"
                  disabled={disableForm && disableForm}
                  id="saveButton"
                >
                  {saveText(medicalConsultationDetails)}
                </Button>
              </Form.Item>
            </Col>
            {medicalConsultationDetails?.started_at && (
              <Fragment>
                <Col xxl={4} lg={6} xs={24}>
                  <Form.Item name="saveAndFinalize">
                    <Button
                      loading={loadingConsultation.saveAndFinalize}
                      htmlType="submit"
                      onClick={() => form.setFieldsValue({ saveAndFinalize: true })}
                      className="cmt-button primary outline p-0"
                      type="primary"
                      size="large"
                      disabled={disableForm && disableForm}
                    >
                      Guardar y finalizar
                    </Button>
                  </Form.Item>
                </Col>
                {medicalConsultationDetails?.done_at && (
                  <Col xxl={4} lg={6} xs={24}>
                    <Form.Item name="sign">
                      {pendingExams ? (
                        <Popover
                          title="Exámenes pendientes"
                          trigger="hover"
                          content={
                            <div>
                              {unfinishedExams.map((unfinishedExam) => (
                                <p>{unfinishedExam.name}</p>
                              ))}
                            </div>
                          }
                          overlayClassName="sidebar-tooltip"
                        >
                          <Button id="signButton" htmlType="submit" className="cmt-button disabled" type="primary" size="large" disabled>
                            Firmar
                          </Button>
                        </Popover>
                      ) : (
                        <SignButton
                          countryCode={countryCode}
                          medicalFormType="psychologicalTwo"
                          t={t}
                          loadingConsultation={loadingConsultation}
                          validateCompletedReport={validateCompletedReport}
                          form={form}
                          isUnableToChoose={isUnableToChoose}
                          showConfirmReprovingValues={showConfirmReprovingValues}
                          setShowConfirmReprovingValues={setShowConfirmReprovingValues}
                          isCompanyAuditorSignerEnabled={() => { }}
                          approvedTextList={approvedTextList}
                          medicalConsultationDetails={medicalConsultationDetails}
                          roles={getCookie('roles')}
                          validatedExams={validatedExams}
                          servicesList={servicesList}
                          loading={loading}
                          generatedPrescriptions={generatedPrescriptions}
                          hasDeferredExamPrescription={hasDeferredExamPrescription}
                        />
                      )}
                    </Form.Item>
                  </Col>
                )}
              </Fragment>
            )}
          </HasAccess>
          {medicalConsultationDetails?.started_at && !pendingExams && medicalConsultationDetails.state !== 'signed' && (
            <Col xxl={1} lg={2} md={2} sm={2} xs={24}>
              <Tooltip title="Previsualizar Informe" color="#223A70">
                <Button
                  className="cmt-button secondary outline"
                  type="secondary"
                  icon={<EyeOutlined />}
                  size={'large'}
                  loading={loadingDownload}
                  onClick={() => downloadUnSignedReport(medicalConsultationDetails['id'])}
                />
              </Tooltip>
            </Col>
          )}

          {medicalConsultationDetails?.state === 'signed' && (
            <Fragment>
              <Col xxl={1} lg={2} md={2} sm={2} xs={24}>
                <Tooltip title="Descargar Informe" color="#223A70">
                  <Button
                    className="cmt-button secondary outline pl-5"
                    type="secondary"
                    icon={<span className="icon-system-download" />}
                    size={'large'}
                    loading={loadingDownload}
                    onClick={() => downloadSignedReport('download', medicalConsultationDetails['id'], 'signed')}
                  />
                </Tooltip>
              </Col>
              <Col xxl={1} lg={2} md={2} sm={2} xs={24}>
                <Tooltip title="Ver Informe Firmado" color="#223A70">
                  <Button
                    className="cmt-button secondary outline pl-5"
                    type="secondary"
                    icon={<EyeOutlined />}
                    size={'large'}
                    loading={loadingDownload}
                    onClick={() => downloadSignedReport('open', medicalConsultationDetails['id'], 'signed')}
                  />
                </Tooltip>
              </Col>
            </Fragment>
          )}
        </Row>
      </Form>
      <ConclusionsModal recommendatiosModal={recommendatiosModal} />
    </Fragment>
  );
};

export default PsychologicalTwo;
